import React, { useState, useEffect, useRef } from "react";
import { components } from "react-select";

import {
  Button,
  FlexDiv,
  Label,
  SVGSprite,
  CountryDropdown,
  DropdownIndicator,
  Input,
  Select,
} from "@/components/Atoms";
import { formatOptionLabel } from "@/components/Molecules/FormatOptionLabel";
import Loader from "@/components/Molecules/Loader";
import CTASuccessCallbackView from "@/components/Molecules/Footer/RightSection";

import Typography from "@/foundation/Typography/V1";
import theme from "@/foundation/Theme";

import { useAppDispatch, useAppSelector } from "@/hooks";

import {
  ArrowDown,
  BodyWrap,
  BoldText,
  CountryPlaceholder,
  DropDown,
  ErrorDiv,
  FormBox,
  InputGroup,
  InputWrap,
  LeadInput,
  NumberPlaceholder,
  PhoneIcon,
  PromoCode,
  PromoCodeApplyButton,
  RotateCheck,
  SelectGroup,
  SelectIcon,
  SubHeading,
} from "./style";

const ValueContainer = ({ children, ...props }) => {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        {children && (
          <SelectIcon
            icon="icon-dart"
            fill={theme.colors.quote}
            height="19px"
            width="19px"
          />
        )}
        {children}
      </components.ValueContainer>
    )
  );
};

export const SuccessModal = ({
  showButton = true,
  showAppDownLoadButtonLink,
  callbackSuccessTitle = "Thank you for showing interest!",
  callbackSuccessSubtitle = "You will get a callback within 24 hours.",
  onHideNutritionCallbackModal = () => {},
  leadData,
}) => (
  <FlexDiv
    alignItems={"center"}
    flexDirection={"column"}
    justifyContent={"center"}
    maxWidth={"386px"}
    margin={"0 auto"}
    id="requestCallbackSuccessModal"
    className="callback-message-wrapper"
    aria-labelledby="requestCallbackSuccessModal"
  >
    {leadData?.leadId && (
      <LeadInput id="lead_id" value={leadData?.leadId} onChange={() => {}} />
    )}
    {leadData?.healthGoal && (
      <LeadInput
        id="health_goal"
        value={leadData?.healthGoal}
        onChange={() => {}}
      />
    )}
    <RotateCheck
      icon="icon-how-it-work-check"
      height={"45px"}
      width={"45px"}
      fill={theme.colors.textGradient.type3static}
    />
    <Typography
      variant="heading2"
      textAlign="center"
      color={theme.colors.gray_100}
      mb={"35px"}
    >
      {callbackSuccessTitle}
    </Typography>
    <Typography
      variant="paragraph1"
      textAlign="center"
      color={theme.colors.gray_100}
      mb={showButton ? "25px" : "0px"}
    >
      {callbackSuccessSubtitle}
    </Typography>
    {showButton && (
      <Button
        variant={"primary"}
        onClick={() => onHideNutritionCallbackModal()}
      >
        Okay, thanks!
      </Button>
    )}
    {showAppDownLoadButtonLink && (
      <CTASuccessCallbackView isCTAFormView={true} />
    )}
  </FlexDiv>
);

const FormInputs = ({
  isLoading,
  success,
  error,
  //
  handleChange = () => {},
  handleSelectChange = () => {},
  handleBlur = () => {},
  onSubmitCallback = () => {},
  //
  showLabels = true,
  formTitle = "Interested? Request a callback",
  formSubtitle = "Please leave your details and someone will get in touch within 24hrs!",
  //
  showNameField,
  namePlaceHolder = "Enter your name",
  name,
  nameError,
  //
  showCVIDField,
  cvidPlaceholder = "Enter Club Vistara ID",
  cvid,
  cvidError,
  //
  showPhoneField,
  countryCode,
  mobilePlaceholder = "Your phone number",
  mobile,
  mobileError,
  //
  showEmailField,
  emailPlaceholder = "Your email address",
  email,
  emailError,
  //
  showCompanyNameField,
  companyNamePlaceholder = "Company Name",
  companyName,
  companyNameError,
  //
  showGoalField,
  goalOptions = [],
  goalPlaceholder = "Select a health goal",
  goal,
  goalError,
  //
  showSelectPackage,
  isPackageFetching,
  packageOptions = [],
  selectedPackagePlaceholder = "Select a package",
  selectedPackage,
  selectedPackageError,
  //
  showPromoCodeField,
  promoCodePlaceholder = "Enter Promo Code",
  promoCode,
  promoCodeError,
  promoCodeButtonText,
  isPromoCodeChecking,
  isPromoCodeApplied,
  promoCodeData,
  applyPromoCode,
  //
  showReferralField,
  referralCodePlaceholder = "Enter Referral Code",
  referralCode,
  //
  showDermaTypeField,
  dermaTypeOptions = [],
  dermaTypePlaceholder = "Select a type",
  dermaType,
  dermaTypeError,
  //
  primaryCTA,
  showSecondaryCTA,
  secondaryCTA,
  classInfo,
  needMoreInfo,
  //
  forwardedRef, //TODO: Confirm Use of This
}) => {
  const dispatch = useAppDispatch();

  const wrapperRef = useRef(null);

  function useOutsideAlerter(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowCountryDropDown(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }
  useOutsideAlerter(wrapperRef);

  const { countryCodeData: countryCodes } = useAppSelector(
    (state) => state.dashboard
  );

  const [showCountryDropDown, setShowCountryDropDown] = useState(false);

  const FormHeader = (
    <>
      {!success && error && (
        <FlexDiv className="text-center text-error mb-1">{error}</FlexDiv>
      )}
      <FlexDiv flexDirection="column" alignItems="center" mb="29px">
        <Typography
          variant="heading2"
          mb="8px"
          color={theme.colors.gray_100}
          textAlign="center"
        >
          {formTitle}
        </Typography>
        <SubHeading variant="paragraph2" textAlign="center">
          {formSubtitle}
        </SubHeading>
      </FlexDiv>
    </>
  );

  const NameField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            variant="span"
            fontFamily="var(--font-700)"
          >
            NAME
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={namePlaceHolder}
          name="name"
          value={name}
          onChange={(name) => handleChange(name)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-user-face"
          iconFill={theme.colors.quote}
          inputPadding="12px 15px 12px 40px"
          iconDimension="20px"
          inputFontSize="16px"
        />
        {nameError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {nameError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const CVIDField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            variant="span"
            fontFamily="var(--font-700)"
          >
            CLUB VISTARA ID
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={cvidPlaceholder}
          name="cvid"
          value={cvid}
          onChange={(cvid) => handleChange(cvid)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-vistara-id"
          iconFill={theme.colors.quote}
          inputPadding="12px 15px 12px 40px"
          iconDimension="25px"
          inputFontSize="16px"
        />
        {cvidError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {cvidError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const PhoneField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            variant="span"
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
          >
            PHONE NUMBER
          </Typography>
        </Label>
      )}

      <InputGroup showLabels={showLabels}>
        <InputWrap>
          <CountryPlaceholder showCountryDropDown={showCountryDropDown}>
            <NumberPlaceholder
              onClick={() => setShowCountryDropDown(!showCountryDropDown)}
              position="relative"
            >
              <PhoneIcon
                icon="icon-phone"
                fill={theme.colors.quote}
                height="19px"
                width="19px"
              />
              <Typography
                variant="title2"
                color={theme.colors.placeholderGrey}
                fontSize="14px"
                fontFamily="var(--font-500)"
                width="100%"
                textAlign="right"
                pr="5px"
              >
                {countryCode}
              </Typography>
              <ArrowDown
                icon="icon-arrow-down"
                fill={theme.colors.quote}
                height="8px"
                width="8px"
              />
            </NumberPlaceholder>
            <DropDown
              showCountryDropDown={showCountryDropDown}
              ref={wrapperRef}
            >
              <CountryDropdown
                menuIsOpen={showCountryDropDown}
                options={countryCodes?.list}
                formatOptionLabel={formatOptionLabel}
                wrapperClassName="shyftList"
                dropDownHeight="180px"
                placeholder="Search"
                defaultValue={countryCode}
                onChange={(countryCode) => {
                  handleSelectChange("countryCode", countryCode.code);
                  setShowCountryDropDown(false);
                }}
                closeMenuOnSelect
              />
            </DropDown>
          </CountryPlaceholder>
          <Input
            inputPadding="13px 15px 13px 111px"
            type="text"
            placeholder={mobilePlaceholder}
            pattern="[0-9]*"
            inputMode="decimal"
            name="mobile"
            value={mobile}
            onChange={(mobile) => handleChange(mobile)}
            onBlur={handleBlur}
            autoComplete="off"
            ref={forwardedRef}
            required
          />
        </InputWrap>
        {mobileError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {mobileError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const EmailField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            EMAIL ADDRESS
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="email"
          placeholder={emailPlaceholder}
          name="email"
          value={email}
          onChange={(email) => handleChange(email)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-envelope"
          iconFill={theme.colors.quote}
          inputPadding="12px 15px 12px 40px"
          iconDimension="20px"
          inputFontSize="16px"
        />
        {emailError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {emailError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const CompanyNameField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="companyName">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            Company Name
          </Typography>
        </Label>
      )}
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={companyNamePlaceholder}
          name="companyName"
          value={companyName}
          onChange={(companyName) => handleChange(companyName)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          required
          icon="icon-briefcase"
          iconFill={theme.colors.quote}
          inputPadding="12px 15px 12px 40px"
          iconDimension="20px"
          inputFontSize="16px"
        />
        {companyNameError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {companyNameError}
            </Typography>
          </ErrorDiv>
        )}
      </InputGroup>
    </FlexDiv>
  );

  const GoalField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            YOUR GOAL
          </Typography>
        </Label>
      )}
      <SelectGroup>
        <Select
          options={goalOptions}
          dropDownHeight="150px"
          placeholder={goalPlaceholder}
          name="goal"
          value={goalOptions.find((o) => o.value === goal) || ""}
          onChange={(goal) => handleSelectChange("goal", goal.value)}
          components={{ ValueContainer, DropdownIndicator }}
          containerPadding="12px 15px 12px 40px"
          // menuIsOpen
          dropdownPos="top"
          borderRadius="10px"
          singleValueColor={theme.colors.gray_90}
          isSearchable={false}
          minHeight="54px"
          onBlur={handleBlur}
        />
        {goalError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {goalError}
            </Typography>
          </ErrorDiv>
        )}
      </SelectGroup>
    </FlexDiv>
  );

  const DermaTypeField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            Type
          </Typography>
        </Label>
      )}
      <SelectGroup>
        <Select
          options={dermaTypeOptions}
          dropDownHeight="150px"
          placeholder={dermaTypePlaceholder}
          name="dermaType"
          value={dermaTypeOptions.find((o) => o.value === dermaType) || ""}
          onChange={(dermaType) =>
            handleSelectChange("dermaType", dermaType.value)
          }
          components={{ ValueContainer, DropdownIndicator }}
          containerPadding="12px 15px 12px 40px"
          // menuIsOpen
          dropdownPos="top"
          borderRadius="10px"
          singleValueColor={theme.colors.gray_90}
          isSearchable={false}
          minHeight="54px"
          onBlur={handleBlur}
        />
        {dermaTypeError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {dermaTypeError}
            </Typography>
          </ErrorDiv>
        )}
      </SelectGroup>
    </FlexDiv>
  );

  const PackagesField = (
    <FlexDiv flexDirection="column">
      {showLabels && (
        <Label htmlFor="name">
          <Typography
            color={theme.colors.gray_80}
            fontFamily="var(--font-700)"
            variant="span"
          >
            SELECT PACKAGE
          </Typography>
        </Label>
      )}
      <SelectGroup>
        <Select
          options={packageOptions}
          dropDownHeight="150px"
          placeholder={selectedPackagePlaceholder}
          name="selectedPackage"
          value={packageOptions?.find((o) => o.value === selectedPackage) || ""}
          onChange={(_package) =>
            handleSelectChange("selectedPackage", _package.value)
          }
          components={{ ValueContainer, DropdownIndicator }}
          containerPadding="12px 15px 12px 40px"
          // menuIsOpen
          dropdownPos="top"
          borderRadius="10px"
          singleValueColor={theme.colors.gray_90}
          isSearchable={false}
          minHeight="54px"
          isDisabled={!goal}
          isLoading={isPackageFetching}
          onBlur={handleBlur}
        />
        {selectedPackageError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
            >
              {selectedPackageError}
            </Typography>
          </ErrorDiv>
        )}
      </SelectGroup>
    </FlexDiv>
  );

  const PromoCodeField = (
    <FlexDiv flexDirection="column">
      <PromoCode showLabels={showLabels}>
        <Input
          type="text"
          placeholder={promoCodePlaceholder}
          name="promoCode"
          value={promoCode?.toUpperCase()}
          onChange={(e) => handleChange(e)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          inputColor={
            isPromoCodeApplied ? theme.colors.link2Color : theme.colors.gray_90
          }
          icon={isPromoCodeApplied ? "icon-success-check" : "icon-discount"}
          iconFill={
            isPromoCodeApplied ? theme.colors.link2Color : theme.colors.quote
          }
          inputPadding="12px 90px 12px 40px"
          iconDimension="20px"
          inputFontSize="16px"
          disabled={isPromoCodeApplied}
        >
          <PromoCodeApplyButton
            variant="link"
            fontSize="13px"
            TextColor={
              isPromoCodeApplied || promoCode?.length > 3
                ? theme.colors.link2Color
                : theme.colors.quote
            }
            onClick={(e) => {
              e.preventDefault();
              applyPromoCode();
            }}
            fontFamily="var(--font-700)"
          >
            {promoCodeButtonText}
            {isPromoCodeChecking && (
              <>
                &nbsp; <Loader />
              </>
            )}
          </PromoCodeApplyButton>
        </Input>
        {promoCodeError && (
          <ErrorDiv alignItems="center">
            <SVGSprite
              icon="icon-info-icon"
              height="14px"
              width="14px"
              fill={theme.colors.red}
              mr="7px"
            />
            <Typography
              variant="span"
              color={theme.colors.red}
              fontFamily="var(--font-700)"
              flex={1}
            >
              {promoCodeError}
            </Typography>
          </ErrorDiv>
        )}
      </PromoCode>
    </FlexDiv>
  );

  const ReferralField = (
    <FlexDiv flexDirection="column">
      <InputGroup showLabels={showLabels}>
        <Input
          type="text"
          placeholder={referralCodePlaceholder}
          name="referralCode"
          value={referralCode}
          onChange={(e) => handleChange(e)}
          onBlur={handleBlur}
          autoComplete="off"
          ref={forwardedRef}
          inputColor={theme.colors.gray_90}
          icon="icon-discount"
          iconFill={theme.colors.quote}
          inputPadding="12px 90px 12px 40px"
          iconDimension="20px"
          inputFontSize="16px"
        />
      </InputGroup>
    </FlexDiv>
  );

  const PrimaryCTA = (
    <FlexDiv id="floatingCta" justifyContent="center">
      <Button
        variant="primary"
        onClick={(e) => {
          e.preventDefault();
          onSubmitCallback(false);
        }}
        width={isPromoCodeApplied && promoCodeData ? "100%" : ""}
        btnPadding={
          isPromoCodeApplied && promoCodeData ? "11px 20px" : "11px 40px"
        }
        textAlign="center"
        fontSize="15px"
      >
        {primaryCTA}
        {isLoading && (
          <>
            &nbsp; <Loader />
          </>
        )}
      </Button>
    </FlexDiv>
  );

  const SecondaryCTA = (
    <FlexDiv alignItems="center" mt="16px" flexDirection="column">
      <Typography
        variant="span"
        fontFamily="var(--font-700)"
        color={theme.colors.quote}
        mb="16px"
        textAlign="center"
      >
        OR
      </Typography>
      <Button
        hoverColor={`${theme.colors.quote}2e`}
        bgColor={`${theme.colors.quote}2e`}
        variant="primary1"
        fontFamily="var(--font-700)"
        TextColor={theme.colorsV2.shyft.purple}
        onClick={(e) => {
          e.preventDefault();
          onSubmitCallback(true);
        }}
        btnPadding="11px 20px"
        textAlign="center"
        fontSize="15px"
      >
        {secondaryCTA}
      </Button>
    </FlexDiv>
  );

  const ClassInfo = (
    <SubHeading
      mt="10px"
      variant="paragraph2"
      textAlign="center"
      fontSize="10px"
    >
      {classInfo}
    </SubHeading>
  );

  const NeedMoreInfo = (
    <FlexDiv justifyContent="center" textAlign="center">
      <BoldText
        TextColor={theme.colors.gray_50}
        variant="link"
        onClick={(e) => {
          e.preventDefault();
          onSubmitCallback(true);
        }}
        fontFamily="var(--font-700)"
      >
        {needMoreInfo}
      </BoldText>
    </FlexDiv>
  );

  return (
    <BodyWrap className="callback-form-containers" width="100%">
      <FlexDiv flexDirection="column" width="100%">
        {FormHeader}
        <FormBox onSubmit={(e) => e.preventDefault()}>
          {showNameField && NameField}
          {showCVIDField && CVIDField}
          {showPhoneField && PhoneField}
          {showEmailField && EmailField}
          {showCompanyNameField && CompanyNameField}
          {showGoalField && GoalField}
          {showDermaTypeField && DermaTypeField}
          {showSelectPackage && PackagesField}
          {showPromoCodeField && PromoCodeField}
          {showReferralField && ReferralField}
          {PrimaryCTA}
          {showSecondaryCTA && SecondaryCTA}
          {classInfo && ClassInfo}
          {needMoreInfo && NeedMoreInfo}
        </FormBox>
      </FlexDiv>
    </BodyWrap>
  );
};

export default FormInputs;
